import { Divider, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { ICandidate, ISubmissionSettingsResponse } from 'wisestep-api';
import { WsColors, WsTypoColors, WsTypography, WsTypoVariants } from 'wisestep-base';

import { useWorkspaceContext } from '../../../../../context';
import useGetSubmissionSettingsOfJob from '../../../../../features/candidateLayout/hooks/useGetSubmissionSettingsOfJob';
import {
  getSubmissionDetailsWithComponent,
  isValidDash,
  SUBMISSION_TYPE,
} from '../../../../../features/candidateLayout/utils/CandidateHelper';
import useGetJob from '../../../../../features/emailSettings/hooks/useGetJob';
import { ExpandableTypography, CompressedLink, ExpandableChips } from '../../childComponets';

import CandidateSummary from './CandidateSummary';
import SubmissionNotesAndEmail from './SubmissionNotesAndEmail';

const renderComponent = (type: SUBMISSION_TYPE, value: string | string[], label: string) => {
  const val = Array.isArray(value) ? value?.[0] : value;
  if (val === '-') return <ExpandableTypography content={val} />;
  switch (type) {
    case SUBMISSION_TYPE.TEXT:
      return <ExpandableTypography content={value as string} />;

    case SUBMISSION_TYPE.LINK: {
      const [label, url] = value as string[];
      return <CompressedLink label={label} url={url} />;
    }

    case SUBMISSION_TYPE.CHIP: {
      const chips = Array.isArray(value) ? value : [value];
      return <ExpandableChips items={chips} label={label} />;
    }

    default:
      return <Typography>{value}</Typography>;
  }
};

const SubmissionDetails = ({ submission }: { submission: ICandidate }) => {
  const { job } = useGetJob(submission?.jobId ?? '');
  const workspace = useWorkspaceContext();
  const { submissionSettings } = useGetSubmissionSettingsOfJob(
    workspace?.id ?? '',
    job?.submissionFormTemplete ?? ''
  );
  const submissionDetails = useMemo(
    () =>
      getSubmissionDetailsWithComponent(
        submission,
        submissionSettings?.fields ?? ({} as ISubmissionSettingsResponse)
      ),
    [submission, submissionSettings?.fields]
  );
  const [showMissingInfo, setShowMissingInfo] = useState<boolean>(false);
  const toggleInfo = () => setShowMissingInfo((prev) => !prev);
  return (
    <Grid display="flex" direction="column" rowGap={1}>
      <CandidateSummary submission={submission} jobDescription={job?.description ?? ''} />
      <Grid display="flex" columnGap={1.5}>
        <Grid xs={5} border={`1.5px solid ${WsColors.NEUTRAL_100}`}>
          <Grid p={1}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
              Submission details
            </WsTypography>
          </Grid>
          <Divider sx={{ borderColor: WsColors.NEUTRAL_100 }} />
          <Grid display="flex" direction="column" xs={12} p={1} rowGap={1}>
            {submissionDetails
              .filter(({ value }) => showMissingInfo || !isValidDash(value))
              .map(({ label, value, component }, index) => (
                <Grid display="flex" alignItems="center" key={index} columnGap={1}>
                  <Grid item xs={6}>
                    <WsTypography color={WsTypoColors.TEXTLIGHT}>{label}</WsTypography>
                  </Grid>
                  <Grid item xs={6}>
                    {renderComponent(component, value, label)}
                  </Grid>
                </Grid>
              ))}

            <Grid xs={12}>
              <FormControlLabel
                sx={{ columnGap: 1, m: 0 }}
                control={<Switch checked={showMissingInfo} onChange={toggleInfo} />}
                label="Show missing info"
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={7} border={`1.5px solid ${WsColors.NEUTRAL_100}`}>
          <SubmissionNotesAndEmail submission={submission} />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SubmissionDetails;
